import React, { useEffect, useState } from 'react';
import { X, AlertCircle } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { validateEmail } from '../../utils/emailValidation';

interface ChangeEmailModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentEmail: string;
  onSubmit: (_newEmail: string, _password: string) => Promise<{ success: boolean; error?: string }>;
}

const ChangeEmailModal: React.FC<ChangeEmailModalProps> = ({
  isOpen,
  onClose,
  currentEmail,
  onSubmit,
}) => {
  const [newEmail, setNewEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [error, setError] = useState('');
  const [emailError, setEmailError] = useState('');
  const [emailSuggestion, setEmailSuggestion] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { user } = useAuth();

  useEffect(() => {
    if (isOpen) {
      setNewEmail('');
      setPassword('');
      setShowCurrentPassword(false);
      setError('');
      setEmailError('');
      setEmailSuggestion('');
      setIsSubmitting(false);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleClose = () => {
    if (isSubmitting) return;
    onClose();
  };

  const handleSubmit = async () => {
    const trimmed = newEmail.trim().toLowerCase();
    if (!trimmed) {
      setEmailError('Please enter an email address.');
      return;
    }
    const validation = validateEmail(trimmed, { blockDisposable: true, checkTypo: false });
    if (!validation.ok) {
      setEmailError(validation.error || 'Please enter a valid email address.');
      return;
    }
    if (trimmed === currentEmail.trim().toLowerCase()) {
      setEmailError('New email must be different from your current email.');
      return;
    }
    if (emailError) return;
    if (!password) {
      setError('Current password is required.');
      return;
    }

    setIsSubmitting(true);
    setError('');
    const result = await onSubmit(trimmed, password);
    if (!result.success) {
      setError(result.error || 'Failed to update email. Please try again.');
      setIsSubmitting(false);
    }
  };

  const handleNewEmailChange = (value: string) => {
    setNewEmail(value);
    setError('');

    const trimmed = value.trim().toLowerCase();
    if (!trimmed) {
      setEmailError('');
      setEmailSuggestion('');
      return;
    }

    // Inline format + disposable validation
    const validation = validateEmail(trimmed, { blockDisposable: true, checkTypo: false });
    if (!validation.ok) {
      setEmailError(validation.error || 'Please enter a valid email address.');
      setEmailSuggestion('');
      return;
    }

    // Check if same as current email
    if (trimmed === currentEmail.trim().toLowerCase()) {
      setEmailError('New email must be different from your current email.');
      setEmailSuggestion('');
      return;
    }

    setEmailError('');

    // Typo suggestion check
    const typoCheck = validateEmail(trimmed, { blockDisposable: false, checkTypo: true });
    setEmailSuggestion(typoCheck.ok && typoCheck.suggestion ? typoCheck.suggestion : '');
  };

  const acceptEmailSuggestion = () => {
    if (!emailSuggestion) return;
    setNewEmail(emailSuggestion);
    setEmailSuggestion('');
    setError('');
  };

  return (
    <div className="modal-overlay zoomOutLevel">
      <div className="modal-content" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <div className="flex md:items-start items-center md:flex-col flex-row">
            <button onClick={onClose} className="md:hidden block mr-3">
              <svg
                viewBox="0 0 24 24"
                fill="currentColor"
                className="back-icon"
              >
                <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z" />
              </svg>
            </button>
            <h2 className="modal-title">Change Email</h2>
            <p className="modal-subtitle md:block hidden">
              You&apos;ll receive a verification link at the new address and be signed out.
            </p>
          </div>
          <button
            type="button"
            onClick={handleClose}
            className="modal-close"
            disabled={isSubmitting}
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="modal-form">
          <div className="form-group">
            <label className="form-label">Current Email</label>
            <div className="register-input-with-icon">
              <img
                src={`${process.env.PUBLIC_URL}/SVGIcons/Email.svg`}
                alt="Mail"
                className="register-input-icon"
              />
              <input
                type="email"
                className="register-input email-input"
                value={currentEmail}
                readOnly
                disabled
              />
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">New Email <span className="text-red-500">*</span></label>
            <div className="register-input-with-icon">
              <img
                src={`${process.env.PUBLIC_URL}/SVGIcons/Email.svg`}
                alt="Mail"
                className="register-input-icon"
              />
              <input
                type="email"
                className={`register-input email-input ${emailError || error ? 'input-error-border' : ''}`}
                placeholder="Enter new email"
                value={newEmail}
                onChange={e => handleNewEmailChange(e.target.value)}
                disabled={isSubmitting}
              />
            </div>
            {emailError && (
              <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
                <AlertCircle className="h-4 w-4 flex-shrink-0" />
                {emailError}
              </p>
            )}
            {!emailError && emailSuggestion && (
              <p className="text-sm mt-1 text-[var(--color-text)]">
                Did you mean{' '}
                <button
                  type="button"
                  onClick={acceptEmailSuggestion}
                  className="font-semibold underline text-[#22c55e] hover:opacity-80"
                >
                  {emailSuggestion}
                </button>
                ?
              </p>
            )}
          </div>

          <div className="form-group">
            {/*
              Label depends on whether the account has a password SET, not on
              loginType. A social user who previously set a password stays
              loginType="google" but is now hybrid — so we ask for their
              Current Password, same as a native credentials user. Only a
              fresh social user with no local password sees "New Password".
            */}
            <label className="form-label">{user?.hasPassword ? 'Current Password' : 'New Password'} <span className="text-red-500">*</span></label>
            <div className="register-input-with-icon">
              <img
                src={`${process.env.PUBLIC_URL}/SVGIcons/tabler_lock-filled.svg`}
                alt="Lock"
                className="register-input-icon"
              />
              <input
                type={showCurrentPassword ? 'text' : 'password'}
                className={`register-input email-input ${error ? 'input-error-border' : ''}`}
                placeholder={user?.hasPassword ? 'Enter your current password' : 'Set a password for this account'}
                value={password}
                onChange={e => { setPassword(e.target.value); setError(''); }}
                disabled={isSubmitting}
              />
              <button
                type="button"
                className="register-password-toggle"
                onClick={() => setShowCurrentPassword(p => !p)}
                tabIndex={-1}
                disabled={isSubmitting}
              >
                {showCurrentPassword ? (
                  <svg width="22" height="15" viewBox="0 0 22 15" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M11 4.5C10.2044 4.5 9.44129 4.81607 8.87868 5.37868C8.31607 5.94129 8 6.70435 8 7.5C8 8.29565 8.31607 9.05871 8.87868 9.62132C9.44129 10.1839 10.2044 10.5 11 10.5C11.7956 10.5 12.5587 10.1839 13.1213 9.62132C13.6839 9.05871 14 8.29565 14 7.5C14 6.70435 13.6839 5.94129 13.1213 5.37868C12.5587 4.81607 11.7956 4.5 11 4.5ZM11 12.5C9.67392 12.5 8.40215 11.9732 7.46447 11.0355C6.52678 10.0979 6 8.82608 6 7.5C6 6.17392 6.52678 4.90215 7.46447 3.96447C8.40215 3.02678 9.67392 2.5 11 2.5C12.3261 2.5 13.5979 3.02678 14.5355 3.96447C15.4732 4.90215 16 6.17392 16 7.5C16 8.82608 15.4732 10.0979 14.5355 11.0355C13.5979 11.9732 12.3261 12.5 11 12.5ZM11 0C6 0 1.73 3.11 0 7.5C1.73 11.89 6 15 11 15C16 15 20.27 11.89 22 7.5C20.27 3.11 16 0 11 0Z" fill="#B5BBC0" />
                  </svg>
                ) : (
                  <svg width="22" height="19" viewBox="0 0 22 19" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M10.83 6L14 9.16V9C14 8.20435 13.6839 7.44129 13.1213 6.87868C12.5587 6.31607 11.7956 6 11 6H10.83ZM6.53 6.8L8.08 8.35C8.03 8.56 8 8.77 8 9C8 9.79565 8.31607 10.5587 8.87868 11.1213C9.44129 11.6839 10.2044 12 11 12C11.22 12 11.44 11.97 11.65 11.92L13.2 13.47C12.53 13.8 11.79 14 11 14C9.67392 14 8.40215 13.4732 7.46447 12.5355C6.52678 11.5979 6 10.3261 6 9C6 8.21 6.2 7.47 6.53 6.8ZM1 1.27L3.28 3.55L3.73 4C2.08 5.3 0.78 7 0 9C1.73 13.39 6 16.5 11 16.5C12.55 16.5 14.03 16.2 15.38 15.66L15.81 16.08L18.73 19L20 17.73L2.27 0M11 4C12.3261 4 13.5979 4.52678 14.5355 5.46447C15.4732 6.40215 16 7.67392 16 9C16 9.64 15.87 10.26 15.64 10.82L18.57 13.75C20.07 12.5 21.27 10.86 22 9C20.27 4.61 16 1.5 11 1.5C9.6 1.5 8.26 1.75 7 2.2L9.17 4.35C9.74 4.13 10.35 4 11 4Z" fill="#B5BBC0" />
                  </svg>
                )}
              </button>
            </div>
          </div>

          {error && (
            <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
              <AlertCircle className="h-4 w-4 flex-shrink-0" />
              {error}
            </p>
          )}
        </div>

        <div className="modal-actions">
          <button
            type="button"
            className="modal-button secondary"
            onClick={handleClose}
            disabled={isSubmitting}
          >
            Cancel
          </button>
          <button
            type="button"
            className="modal-button primary"
            onClick={handleSubmit}
            disabled={isSubmitting || !!emailError || !newEmail.trim()}
          >
            {isSubmitting ? 'Updating…' : 'Update'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ChangeEmailModal;
